import { useEffect, useState } from 'react';

// Tracks an element's `clientWidth` — the padding box. That exact quantity, not `contentRect`,
// because MUI's Autocomplete sizes its own suggestions dropdown with `anchorEl.clientWidth`, and the
// overflow Popover has to come out the same width as that dropdown. Measuring the same thing makes
// them equal by construction, down to the integer rounding, instead of agreeing only until someone
// changes a padding.
//
// Hand-rolled on purpose: `useObserverTags` (the grid's overflow hook) is coupled to the AG Grid
// cell renderer (Immutable OrderedMap + a per-element IntersectionObserver) and da-19657 design.md
// rules out reusing it here, while the two ResizeObserver wrappers in package.json
// (`react-resize-observer`, `@react-hook/resize-observer`) are single-use rudiments over a native
// API that needs no wrapper.
//
// Returns a CALLBACK ref, not a RefObject: assigning `ref.current` does not re-run an effect, so
// observation would silently never start for a node that mounts after the first render.
const useElementWidth = <T extends HTMLElement>(): [(node: T | null) => void, number] => {
  const [node, setNode] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!node) return undefined;
    const observer = new ResizeObserver(() => setWidth(node.clientWidth));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, width];
};

export default useElementWidth;
